import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Badge } from 'primereact/badge';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';

const BackendLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filters
    const [filters, setFilters] = useState({
        dateFrom: null,
        dateTo: null,
        method: '',
        isAuthorized: '',
        searchTerm: ''
    });

    const methodOptions = [
        { label: 'All Methods', value: '' },
        { label: 'GET', value: 'GET' },
        { label: 'POST', value: 'POST' },
        { label: 'PUT', value: 'PUT' },
        { label: 'DELETE', value: 'DELETE' },
        { label: 'PATCH', value: 'PATCH' }
    ];

    const authOptions = [
        { label: 'All Requests', value: '' },
        { label: 'Authorized', value: 'true' },
        { label: 'Unauthorized', value: 'false' }
    ];

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const page = Math.floor(first / rows) + 1;
            let url = `/api/core/audit/logs?page=${page}&limit=${rows}`;

            // Add filters to URL
            if (filters.method) url += `&method=${filters.method}`;
            if (filters.isAuthorized) url += `&isAuthorized=${filters.isAuthorized}`;
            if (filters.searchTerm) url += `&search=${encodeURIComponent(filters.searchTerm)}`;
            if (filters.dateFrom) url += `&dateFrom=${filters.dateFrom.toISOString()}`;
            if (filters.dateTo) url += `&dateTo=${filters.dateTo.toISOString()}`;

            console.log('Fetching logs from:', url);

            const response = await makeRequest2(url, 'GET');
            console.log('API Response:', response);

            if (response.success) {
                setLogs(response.data.data || []);
                if (response.data.pagination) {
                    setTotalRecords(response.data.pagination.totalCount || 0);
                }
                console.log('Logs set successfully:', response.data.data);
            } else {
                console.error('API returned unsuccessful response:', response);
                toastify(response?.error || 'Failed to fetch audit logs', 'error');
            }
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            toastify('Error fetching audit logs: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [first, rows]);

    const onPage = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const handleFilterChange = () => {
        setFirst(0);
        fetchLogs();
    };

    const clearFilters = () => {
        setFilters({
            dateFrom: null,
            dateTo: null,
            method: '',
            isAuthorized: '',
            searchTerm: ''
        });
        setFirst(0);
        setTimeout(fetchLogs, 100);
    };

    const showDetails = (log) => {
        setSelectedLog(log);
        setDetailsVisible(true);
    };

    // Column templates
    const timestampTemplate = (rowData) => {
        return new Date(rowData.timestamp).toLocaleString();
    };

    const methodTemplate = (rowData) => {
        const severity = {
            'GET': 'info',
            'POST': 'success',
            'PUT': 'warning',
            'DELETE': 'danger',
            'PATCH': 'secondary'
        };
        return <Tag value={rowData.method} severity={severity[rowData.method] || 'info'} />;
    };

    const authTemplate = (rowData) => {
        return (
            <Badge
                value={rowData.is_authorized ? 'Authorized' : 'Unauthorized'}
                severity={rowData.is_authorized ? 'success' : 'danger'}
            />
        );
    };

    const userTemplate = (rowData) => {
        return rowData.user_details ? (
            <div>
                <div><strong>{rowData.user_details.fullName}</strong></div>
                <small className="text-muted">{rowData.user_details.email}</small>
            </div>
        ) : 'N/A';
    };

    const actionsTemplate = (rowData) => {
        return (
            <Button
                icon="ti ti-eye"
                className="p-button-rounded p-button-info p-button-sm"
                onClick={() => showDetails(rowData)}
                tooltip="View Details"
            />
        );
    };

    const urlTemplate = (rowData) => {
        const url = rowData.url || '';
        return url.length > 50 ? `${url.substring(0, 50)}...` : url;
    };

    return (
        <div className="backend-logs">
            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label">Date From:</label>
                            <Calendar
                                value={filters.dateFrom}
                                onChange={(e) => setFilters({...filters, dateFrom: e.value})}
                                showIcon
                                className="w-100"
                                dateFormat="yy-mm-dd"
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Date To:</label>
                            <Calendar
                                value={filters.dateTo}
                                onChange={(e) => setFilters({...filters, dateTo: e.value})}
                                showIcon
                                className="w-100"
                                dateFormat="yy-mm-dd"
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Method:</label>
                            <Dropdown
                                value={filters.method}
                                options={methodOptions}
                                onChange={(e) => setFilters({...filters, method: e.value})}
                                className="w-100"
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Authorization:</label>
                            <Dropdown
                                value={filters.isAuthorized}
                                options={authOptions}
                                onChange={(e) => setFilters({...filters, isAuthorized: e.value})}
                                className="w-100"
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Search:</label>
                            <InputText
                                value={filters.searchTerm}
                                onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                                placeholder="Search logs..."
                                className="w-100"
                            />
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-12">
                            <Button label="Apply Filters" icon="ti ti-search" onClick={handleFilterChange} className="me-2" />
                            <Button label="Clear Filters" icon="ti ti-times" onClick={clearFilters} className="p-button-secondary me-2" />
                            <Button label="Refresh" icon="ti ti-refresh" onClick={fetchLogs} className="p-button-outlined" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">Backend Audit Logs ({totalRecords} total)</h5>
                </div>
                <div className="card-body">
                    <DataTable
                        value={logs}
                        loading={loading}
                        paginator
                        rows={rows}
                        totalRecords={totalRecords}
                        lazy
                        first={first}
                        onPage={onPage}
                        rowsPerPageOptions={[10, 25, 50]}
                        className="p-datatable-gridlines"
                        emptyMessage="No audit logs found"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                        stripedRows
                        size="small"
                    >
                        <Column
                            field="timestamp"
                            header="Timestamp"
                            body={timestampTemplate}
                            sortable
                            style={{ minWidth: '150px' }}
                        />
                        <Column
                            field="method"
                            header="Method"
                            body={methodTemplate}
                            style={{ minWidth: '80px' }}
                        />
                        <Column
                            field="url"
                            header="URL"
                            body={urlTemplate}
                            style={{ minWidth: '200px' }}
                        />
                        <Column
                            field="ip_address"
                            header="IP Address"
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            field="is_authorized"
                            header="Auth Status"
                            body={authTemplate}
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            field="user_details"
                            header="User"
                            body={userTemplate}
                            style={{ minWidth: '180px' }}
                        />
                        <Column
                            field="activity"
                            header="Activity"
                            style={{ minWidth: '150px' }}
                        />
                        <Column
                            header="Actions"
                            body={actionsTemplate}
                            style={{ minWidth: '80px', textAlign: 'center' }}
                        />
                    </DataTable>
                </div>
            </div>

            {/* Details Dialog */}
            <Dialog
                header="Audit Log Details"
                visible={detailsVisible}
                style={{ width: '90vw', maxWidth: '1200px' }}
                onHide={() => setDetailsVisible(false)}
                maximizable
                modal
            >
                {selectedLog && (
                    <div className="log-details">
                        <div className="row">
                            <div className="col-md-6">
                                <h6 className="text-primary">Request Information</h6>
                                <div className="table-responsive">
                                    <table className="table table-sm">
                                        <tbody>
                                        <tr>
                                            <td><strong>ID:</strong></td>
                                            <td>{selectedLog.id}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Timestamp:</strong></td>
                                            <td>{new Date(selectedLog.timestamp).toLocaleString()}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Method:</strong></td>
                                            <td><Tag value={selectedLog.method} severity="info" /></td>
                                        </tr>
                                        <tr>
                                            <td><strong>URL:</strong></td>
                                            <td className="text-break">{selectedLog.url}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>IP Address:</strong></td>
                                            <td>{selectedLog.ip_address}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Platform:</strong></td>
                                            <td>{selectedLog.platform}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Browser:</strong></td>
                                            <td>{selectedLog.browser}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>OS:</strong></td>
                                            <td>{selectedLog.operating_system}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Device:</strong></td>
                                            <td>{selectedLog.device_type}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Activity:</strong></td>
                                            <td><Badge value={selectedLog.activity} severity="success" /></td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <h6 className="text-primary">User Information</h6>
                                {selectedLog.user_details ? (
                                    <div className="table-responsive">
                                        <table className="table table-sm">
                                            <tbody>
                                            <tr>
                                                <td><strong>Name:</strong></td>
                                                <td>{selectedLog.user_details.fullName}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Email:</strong></td>
                                                <td>{selectedLog.user_details.email}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Role:</strong></td>
                                                <td><Badge value={selectedLog.user_details.role} severity="info" /></td>
                                            </tr>
                                            <tr>
                                                <td><strong>Phone:</strong></td>
                                                <td>{selectedLog.user_details.phoneNumber}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>User ID:</strong></td>
                                                <td className="text-break small">{selectedLog.user_details.userId}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Authorized:</strong></td>
                                                <td>
                                                    <Badge
                                                        value={selectedLog.is_authorized ? 'Yes' : 'No'}
                                                        severity={selectedLog.is_authorized ? 'success' : 'danger'}
                                                    />
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-muted">No user information available</p>
                                )}
                            </div>
                        </div>

                        {selectedLog.request_data && (
                            <div className="mt-4">
                                <h6 className="text-primary">Request Data</h6>
                                <pre className="bg-light p-3 rounded border" style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                    {JSON.stringify(selectedLog.request_data, null, 2)}
                                </pre>
                            </div>
                        )}

                        {selectedLog.previous_data && (
                            <div className="mt-3">
                                <h6 className="text-primary">Previous Data</h6>
                                <pre className="bg-light p-3 rounded border" style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                    {JSON.stringify(selectedLog.previous_data, null, 2)}
                                </pre>
                            </div>
                        )}

                        {selectedLog.custom_data && (
                            <div className="mt-3">
                                <h6 className="text-primary">Custom Data</h6>
                                <pre className="bg-light p-3 rounded border" style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                    {JSON.stringify(selectedLog.custom_data, null, 2)}
                                </pre>
                            </div>
                        )}

                        {selectedLog.route_params && Object.keys(selectedLog.route_params).length > 0 && (
                            <div className="mt-3">
                                <h6 className="text-primary">Route Parameters</h6>
                                <pre className="bg-light p-3 rounded border" style={{ fontSize: '12px', maxHeight: '150px', overflow: 'auto' }}>
                                    {JSON.stringify(selectedLog.route_params, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                )}
                <div className="mt-4 text-end">
                    <Button
                        label="Close"
                        icon="pi pi-times"
                        onClick={() => setDetailsVisible(false)}
                        className="p-button-secondary"
                    />
                </div>
            </Dialog>
        </div>
    );
};

export default BackendLogs;